'use client';

import Icon from '@/components/ui/AppIcon';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
}

const Pagination = ({ currentPage, totalPages, onPageChange, className = '' }: PaginationProps) => {
  const getVisiblePages = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  const visiblePages = getVisiblePages();

  if (totalPages <= 1) return null;

  return (
    <div className={`flex items-center justify-center space-x-2 ${className}`}>
      {/* Previous Button */}
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={`flex items-center space-x-1 px-3 py-2 rounded-industrial border transition-industrial ${
          currentPage === 1
            ? 'border-border text-text-secondary cursor-not-allowed' :'border-border text-foreground hover:bg-muted'
        }`}
      >
        <Icon name="ChevronLeftIcon" size={16} />
        <span className="hidden sm:inline font-heading uppercase tracking-wide text-sm">Previous</span>
      </button>

      {/* Page Numbers */}
      <div className="flex items-center space-x-1">
        {visiblePages.map((page, index) => (
          <div key={index}>
            {page === '...' ? (
              <span className="px-3 py-2 text-text-secondary">...</span>
            ) : (
              <button
                onClick={() => onPageChange(page as number)}
                className={`w-10 h-10 rounded-industrial border transition-industrial font-heading font-bold ${
                  currentPage === page
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border text-foreground hover:bg-muted'
                }`}
              >
                {page}
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Next Button */}
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={`flex items-center space-x-1 px-3 py-2 rounded-industrial border transition-industrial ${
          currentPage === totalPages
            ? 'border-border text-text-secondary cursor-not-allowed' :'border-border text-foreground hover:bg-muted'
        }`}
      >
        <span className="hidden sm:inline font-heading uppercase tracking-wide text-sm">Next</span>
        <Icon name="ChevronRightIcon" size={16} />
      </button>
    </div>
  );
};

export default Pagination;